import React, { useEffect, useMemo, useRef } from 'react';
import { DrawContext, SectionFieldCollection, SectionFieldDTO } from '../magic-report.type';
import { MagicReportSection } from './MagicReportSection';
import { NOTARY_SECTION_CONFIG } from './sections.config';
import { CommentsSection } from './CommentsSection';
import { getCommentsField } from './section-utils';
import { DeclarativeLayoutRenderer, type FieldConfig } from './DeclarativeLayoutRenderer';
import { useMagicReport } from '../context/MagicReportContext';
import {
  buildArchEngSignatureComment,
  buildContractorSignatureComment,
  type AllApplications,
  type ArchitectReviewState,
} from './notary-signatures-comment.util';

interface NotaryAndSignaturesSectionProps {
  magicReportId: string;
}

const CONTRACTOR_TOGGLE = 'ContractorSignature';
const CONTRACTOR_CONTENT = 'ContractorSignatureContent';
const CONTRACTOR_ALL_APPS = 'ContractorAllApplications';
const ARCH_TOGGLE = 'ArchEngSignature';
const ARCH_CONTENT = 'ArchEngSignatureContent';
const ARCH_ALL_APPS = 'ArchEngAllApplications';

/**
 * All/Some is its own single-select toggle in its own field, rendered to the left of the existing
 * buttons on the same row (see `leadingToggle` in DeclarativeLayoutRenderer).
 */
const ALL_APPS_OPTIONS: AllApplications[] = ['All', 'Some'];

/** These toggles store their selections as a comma-joined string (FieldType.Select). */
const parseSelections = (value?: string): string[] =>
  (value || '')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);

/** An empty field (or a report saved before this option) means "All". */
const readAllApplications = (value?: string): AllApplications => (value === 'Some' ? 'Some' : 'All');

/**
 * Notary and Signatures Section Content
 *
 * One set of toggles describes the whole report. When it covers several G702s an extra
 * "All Applications?" toggle sits alongside them, choosing between the plural wording and the
 * "with the exception of the ????? Application" wording — the excepted application being a literal
 * placeholder the reviewer fills in. Single-G702 reports show no All/Some control and keep their
 * original singular wording.
 */
const NotaryAndSignaturesSectionContent: React.FC<{
  data: SectionFieldCollection;
  handleFieldChange: (fieldName: string, updates: Partial<SectionFieldDTO>) => void;
  getField: (fieldName: string) => SectionFieldDTO | undefined;
  onBook?: () => number;
}> = ({ data, handleFieldChange, getField, onBook }) => {
  const { reportState } = useMagicReport();
  const commentsField = getCommentsField(data, NOTARY_SECTION_CONFIG.config);

  const draws: DrawContext[] = useMemo(() => {
    const fromState = reportState.draws as DrawContext[] | undefined;
    if (fromState?.length) return fromState;
    return reportState.drawSummary ? [{ drawId: reportState.drawSummary.id, drawSummary: reportState.drawSummary }] : [];
  }, [reportState.draws, reportState.drawSummary]);

  const isMultiDraw = draws.length > 1;

  const contractorSelections = parseSelections(getField(CONTRACTOR_TOGGLE)?.value);
  const archSelections = parseSelections(getField(ARCH_TOGGLE)?.value);

  const contractorAllApps = readAllApplications(getField(CONTRACTOR_ALL_APPS)?.value);
  const archAllApps = readAllApplications(getField(ARCH_ALL_APPS)?.value);

  const contractorComment = useMemo(
    () =>
      buildContractorSignatureComment({
        isMultiDraw,
        allApplications: contractorAllApps,
        signed: contractorSelections.includes('Signed'),
        notarized: contractorSelections.includes('Notarized'),
      }),
    [isMultiDraw, contractorAllApps, contractorSelections.join(',')],
  );

  const archComment = useMemo(() => {
    const review: ArchitectReviewState = archSelections.includes('Architect Reviewed')
      ? 'reviewing'
      : archSelections.includes('Unsure if Architect Reviewed')
        ? 'unsure'
        : 'notReviewing';
    return buildArchEngSignatureComment({
      isMultiDraw,
      allApplications: archAllApps,
      review,
      certified: archSelections.includes('Signed'),
    });
  }, [isMultiDraw, archAllApps, archSelections.join(',')]);

  // Regenerate each comment when its own toggles change, mirroring the previous behaviour where
  // toggling rewrote the paired text. On mount, saved text is left alone unless it's empty.
  const lastContractorRef = useRef<string | null>(null);
  const lastArchRef = useRef<string | null>(null);

  useEffect(() => {
    const isFirstRun = lastContractorRef.current === null;
    const changed = lastContractorRef.current !== contractorComment;
    lastContractorRef.current = contractorComment;
    if (draws.length === 0) return;
    const isEmpty = !(getField(CONTRACTOR_CONTENT)?.value || '').trim();
    if (isFirstRun && !isEmpty) return;
    if (!isFirstRun && !changed) return;
    handleFieldChange(CONTRACTOR_CONTENT, { value: contractorComment });
  }, [contractorComment]);

  useEffect(() => {
    const isFirstRun = lastArchRef.current === null;
    const changed = lastArchRef.current !== archComment;
    lastArchRef.current = archComment;
    if (draws.length === 0) return;
    const isEmpty = !(getField(ARCH_CONTENT)?.value || '').trim();
    if (isFirstRun && !isEmpty) return;
    if (!isFirstRun && !changed) return;
    handleFieldChange(ARCH_CONTENT, { value: archComment });
  }, [archComment]);

  /**
   * Layout is built here rather than taken from the static config so All/Some can be prepended to
   * each toggle group only for multi-G702 reports. `getDefaultText` is deliberately omitted — the
   * effects above own comment regeneration, since the text depends on All/Some as well.
   */
  const layout = useMemo(() => {
    // Only multi-G702 reports have anything to except, so the All/Some toggle is hidden otherwise.
    const allAppsToggle = (fieldName: string) =>
      isMultiDraw ? { fieldName, options: ALL_APPS_OPTIONS as string[], defaultValue: 'All' } : undefined;

    const singleColumn: FieldConfig[] = [
      {
        fieldName: CONTRACTOR_TOGGLE,
        type: 'multiselect-toggle',
        label: 'Contractor Signature',
        options: ['Signed', 'Notarized'],
        leadingToggle: allAppsToggle(CONTRACTOR_ALL_APPS),
        autoSelectRules: { option: 'Notarized', autoSelect: ['Signed'] },
      },
      { fieldName: CONTRACTOR_CONTENT, type: 'text-paired', label: 'Contractor Signature Content' },
      {
        fieldName: ARCH_TOGGLE,
        type: 'multiselect-toggle',
        label: 'Arch Eng Signature',
        options: ['Signed', 'Architect Reviewed', 'Unsure if Architect Reviewed'],
        leadingToggle: allAppsToggle(ARCH_ALL_APPS),
        mutuallyExclusive: [['Architect Reviewed', 'Unsure if Architect Reviewed']],
        isOptionDisabled: (option: string, selectedValues: string[]) => {
          // Signed is only meaningful while the architect is reviewing
          if (option === 'Signed') return !selectedValues.includes('Architect Reviewed');
          return false;
        },
      },
      { fieldName: ARCH_CONTENT, type: 'text-paired', label: 'Arch Eng Signature Content' },
    ];

    return { singleColumn };
  }, [isMultiDraw]);

  // Handle field deletion (clear content)
  const handleDeleteField = (fieldName: string) => {
    handleFieldChange(fieldName, { value: '', addToActionItems: false });
  };

  return (
    <>
      <DeclarativeLayoutRenderer
        layout={layout}
        getField={getField}
        handleFieldChange={handleFieldChange}
        onDeleteField={handleDeleteField}
        onBook={onBook}
      />

      {/* Comments Section */}
      {commentsField && (
        <CommentsSection
          commentsField={commentsField}
          onChange={updated => handleFieldChange('Comments', updated)}
          title='Comments'
          onBook={onBook}
        />
      )}
    </>
  );
};

/**
 * Notary and Signatures Section
 */
export const NotaryAndSignaturesSection: React.FC<NotaryAndSignaturesSectionProps> = ({ magicReportId }) => {
  return (
    <MagicReportSection
      magicReportId={magicReportId}
      sectionConfig={NOTARY_SECTION_CONFIG}
      customization={{
        renderContent: (data, handleFieldChange, getField, onBook) => (
          <NotaryAndSignaturesSectionContent data={data} handleFieldChange={handleFieldChange} getField={getField} onBook={onBook} />
        ),
      }}
    />
  );
};
